import {TableLastInteractedRowDirective} from '../../../../directives/table-last-interacted-row.directive';
import {DecimalPipe} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Title} from '@angular/platform-browser';
import {PrimeTemplate} from 'primeng/api';
import {ButtonDirective} from 'primeng/button';
import {SelectButtonModule} from 'primeng/selectbutton';
import {Table, TableModule} from 'primeng/table';
import {TooltipModule} from 'primeng/tooltip';
import {ActionButtonComponent} from '../../../../components/form/action-buttons/action-button.component';
import {InputComponent} from '../../../../components/form/input/input.component';
import {CounterBadgeComponent} from '../../../../components/table/counter-badge/counter-badge.component';
import {IntFilterComponent} from '../../../../components/table/filters/int/int-filter.component';
import {StringFilterComponent} from '../../../../components/table/filters/string/string-filter.component';
import {PagePanelComponent} from '../../../../components/panels/page-panel/page-panel.component';
import {BeActiveRawInvoiceHeader} from '../../../../models/invoices/be-active-raw-invoice-header';
import {Routing} from '../../../../models/routing/routing';
import {AppDatePipe} from '../../../../pipes/app-date.pipe';
import {RestApiService} from '../../../../services/api';
import {StringifierService} from '../../../../services/stringifier.service';
import {ToastService} from '../../../../services/toast.service';
import {TargetBlankDirective} from '../../../../directives/target-blank.directive';
import {MenuService} from '../../../../services/menu.service';
import {AnInvoicesMenuComponent} from '../menu/an-invoices-menu.component';

type InvoiceViewFilter = 'invoices' | 'creditNotes' | 'all';

@Component({
    selector: 'app-an-invoices-list',
    imports: [
        TableLastInteractedRowDirective,
        ActionButtonComponent,
        CounterBadgeComponent,
        DecimalPipe,
        InputComponent,
        IntFilterComponent,
        PrimeTemplate,
        FormsModule,
        SelectButtonModule,
        StringFilterComponent,
        TableModule,
        TooltipModule,
        PagePanelComponent,
        AppDatePipe,
        ButtonDirective,
        TargetBlankDirective
    ],
    templateUrl: './an-invoices-list.component.html',
    styleUrls: ['./an-invoices-list.component.scss'],
})
export class AnInvoicesListComponent implements OnInit {
    readonly Routing = Routing;
    readonly invoiceIconSrc = 'assets/images/invoice-icon.png';
    readonly creditNoteIconSrc = 'assets/images/credit-note-icon.png';
    amountDifferenceDelta = 0.05;

    loading = false;
    allInvoices: BeActiveRawInvoiceHeader[] = [];
    invoices: BeActiveRawInvoiceHeader[] = [];
    filteredCount = 0;
    globalSearch = '';
    selectedInvoiceView: InvoiceViewFilter = 'invoices';

    readonly invoiceViewOptions: { label: string; value: InvoiceViewFilter }[] = [
        {label: 'Fatture', value: 'invoices'},
        {label: 'Note di credito', value: 'creditNotes'},
        {label: 'Entrambi', value: 'all'},
    ];

    invoiceStringifier: BeActiveRawInvoiceHeader;

    readonly globalFilterFields: string[];

    constructor(private api: RestApiService,
                private toast: ToastService,
                private titleService: Title,
                private stringifierService: StringifierService,
                private menu: MenuService) {
        this.invoiceStringifier = this.stringifierService.getStringifier(BeActiveRawInvoiceHeader);
        this.globalFilterFields = [
            this.invoiceStringifier.beActiveRawInvoiceHeaderId.toString(),
            this.invoiceStringifier.invoiceNumber.toString(),
            this.invoiceStringifier.invoiceYear.toString(),
            this.invoiceStringifier.invoiceDescription.toString(),
            this.invoiceStringifier.amountWithoutVat.toString(),
            this.invoiceStringifier.calculatedTotalAmountWithoutVat.toString(),
            this.invoiceStringifier.rowsWithErrorsCount.toString(),
        ];
        this.titleService.setTitle('Lista fatture - Phoenix');
    }

    async ngOnInit() {
        this.menu.setMenu({menu: AnInvoicesMenuComponent});
        await this.loadData();
    }

    obj(item: BeActiveRawInvoiceHeader): BeActiveRawInvoiceHeader {
        return item;
    }

    invoiceTypeLabel(invoice: BeActiveRawInvoiceHeader): string {
        return invoice.isCreditNote() ? 'Nota di credito' : 'Fattura';
    }

    getInvoiceTypeIconSrc(invoice: BeActiveRawInvoiceHeader): string {
        return invoice.isCreditNote() ? this.creditNoteIconSrc : this.invoiceIconSrc;
    }

    getAmountDifference(invoice: BeActiveRawInvoiceHeader): number {
        return (invoice.amountWithoutVat ?? 0) - (invoice.calculatedTotalAmountWithoutVat ?? 0);
    }

    hasRelevantAmountDifference(invoice: BeActiveRawInvoiceHeader): boolean {
        return !invoice.isCreditNote() && Math.abs(this.getAmountDifference(invoice)) > this.amountDifferenceDelta;
    }

    async loadData() {
        try {
            this.loading = true;
            const response = await this.api.anInvoices.getAll();
            this.allInvoices = response.data ?? [];
            this.applyInvoiceViewFilter();
            this.filteredCount = this.invoices.length;
        } catch (e) {
            console.error(e);
            this.toast.error('Fatture', 'Errore durante il caricamento delle fatture');
        } finally {
            this.loading = false;
        }
    }

    onInvoiceViewChange(table: Table) {
        this.applyInvoiceViewFilter();
        this.filteredCount = this.invoices.length;
        setTimeout(() => {
            this.filterGlobal(table);
            this.filteredCount = table.filteredValue?.length ?? this.invoices.length;
        });
    }

    private applyInvoiceViewFilter() {
        this.invoices = this.allInvoices.filter(invoice => {
            const isCreditNote = this.isCreditNote(invoice);

            switch (this.selectedInvoiceView) {
                case 'creditNotes':
                    return isCreditNote;
                case 'all':
                    return true;
                case 'invoices':
                default:
                    return !isCreditNote;
            }
        });
    }

    private isCreditNote(invoice: BeActiveRawInvoiceHeader): boolean {
        return (invoice.invoiceAmount ?? invoice.amountWithoutVat ?? invoice.calculatedTotalAmountWithoutVat ?? 0) < 0;
    }

    onFilter(event: { filteredValue?: BeActiveRawInvoiceHeader[] }) {
        this.filteredCount = event.filteredValue?.length ?? this.invoices.length;
    }

    updateGlobalSearch(table: Table, value: string) {
        this.globalSearch = value;
        this.filterGlobal(table);
    }

    filterGlobal(table: Table) {
        table.filterGlobal(this.globalSearch, 'contains');
    }

    clearFilters(table: Table) {
        this.globalSearch = '';
        table.clear();
        this.filteredCount = this.invoices.length;
    }
}
